import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { 
  CreditCard, Search, ArrowLeft, CheckCircle, Clock, 
  AlertTriangle, Calendar, Mail, Phone, Send, CheckSquare, Wallet
} from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../lib/api';

const PaymentsManagement = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTerm, setFilterTerm] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [sendingRequests, setSendingRequests] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const res = await api.get('/dashboard/payments');
      setPayments(res.data.payments || []);
      setSummary(res.data.summary || {});
    } catch (error) {
      toast.error('Failed to load payments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments(prev => 
      prev.includes(paymentId) 
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const selectAllPending = () => {
    const pendingIds = filteredPayments
      .filter(p => p.payment_status === 'pending' || p.is_overdue)
      .map(p => p.id);
    setSelectedPayments(pendingIds);
  };

  const clearSelection = () => {
    setSelectedPayments([]);
  };

  const chargePayments = async () => {
    if (selectedPayments.length === 0) {
      toast.error('Please select at least one payment to charge');
      return;
    }

    setSendingRequests(true);
    try {
      const res = await api.post('/payments/charge', {
        booking_ids: selectedPayments
      });
      
      // Show results
      if (res.data.charged_count > 0) {
        toast.success(`Successfully charged ${res.data.charged_count} payment(s) - Total: £${res.data.total_charged.toFixed(2)}`);
      }
      
      if (res.data.need_card_count > 0) {
        toast.warning(`${res.data.need_card_count} parent(s) don't have saved cards. Sending payment requests instead...`);
        
        // Send payment request emails to those without saved cards
        const needCardIds = res.data.need_card.map(n => n.id);
        if (needCardIds.length > 0) {
          await api.post('/payments/send-requests', { booking_ids: needCardIds });
          toast.info(`Payment request emails sent to ${needCardIds.length} parent(s)`);
        }
      }
      
      if (res.data.failed_count > 0) {
        res.data.failed.forEach(f => {
          toast.error(`Failed: ${f.student || f.id} - ${f.error}`);
        });
      }
      
      setSelectedPayments([]);
      fetchPayments(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payments');
    } finally {
      setSendingRequests(false);
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.parent_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.parent_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.class_names?.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesTerm = filterTerm === 'all' || payment.term_length === filterTerm;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'paid' && payment.payment_status === 'paid') ||
      (filterStatus === 'pending' && payment.payment_status === 'pending') ||
      (filterStatus === 'overdue' && payment.is_overdue);
    
    return matchesSearch && matchesTerm && matchesStatus;
  });

  const formatPrice = (amount) => `£${(amount || 0).toFixed(2)}`;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getTermBadge = (termLength) => {
    if (termLength === '12weeks') {
      return (
        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
          12 Week Full Term
        </Badge>
      );
    }
    return (
      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
        6 Week Half Term
      </Badge>
    );
  };

  const getStatusBadge = (payment) => {
    if (payment.is_overdue) {
      return (
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Renewal Due
        </Badge>
      );
    }
    if (payment.payment_status === 'paid') {
      return (
        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
          <CheckCircle className="w-3 h-3 mr-1" />
          Paid
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
        <Clock className="w-3 h-3 mr-1" />
        Pending
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF007F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin')}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <h1 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
          <CreditCard className="w-8 h-8 text-[#FF007F]" />
          Payments Management
        </h1>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${filterStatus === 'all' && filterTerm === 'all' ? 'border-[#FF007F] ring-1 ring-[#FF007F]' : 'hover:border-[#FF007F]/50'}`}
            onClick={() => { setFilterStatus('all'); setFilterTerm('all'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">Total</p>
              <p className="text-2xl font-bold text-[#FF007F]">{summary.total_payments || 0}</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${filterTerm === '6weeks' && filterStatus === 'paid' ? 'border-blue-500 ring-1 ring-blue-500' : 'hover:border-blue-500/50'}`}
            onClick={() => { setFilterTerm('6weeks'); setFilterStatus('paid'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">6 Week (Paid)</p>
              <p className="text-2xl font-bold text-blue-400">{summary.paid_6week_count || 0}</p>
              <p className="text-blue-400/70 text-sm">{formatPrice(summary.paid_6week_total)}</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${filterTerm === '12weeks' && filterStatus === 'paid' ? 'border-purple-500 ring-1 ring-purple-500' : 'hover:border-purple-500/50'}`}
            onClick={() => { setFilterTerm('12weeks'); setFilterStatus('paid'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">12 Week (Paid)</p>
              <p className="text-2xl font-bold text-purple-400">{summary.paid_12week_count || 0}</p>
              <p className="text-purple-400/70 text-sm">{formatPrice(summary.paid_12week_total)}</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${filterStatus === 'pending' ? 'border-yellow-500 ring-1 ring-yellow-500' : 'hover:border-yellow-500/50'}`}
            onClick={() => { setFilterStatus('pending'); setFilterTerm('all'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-400">{summary.pending_count || 0}</p>
              <p className="text-yellow-400/70 text-sm">{formatPrice(summary.pending_total)}</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`bg-[#111] border-[#333] cursor-pointer transition-all ${filterStatus === 'overdue' ? 'border-red-500 ring-1 ring-red-500' : 'hover:border-red-500/50'}`}
            onClick={() => { setFilterStatus('overdue'); setFilterTerm('all'); }}
          >
            <CardContent className="p-4">
              <p className="text-white/50 text-sm">Renewal Due</p>
              <p className="text-2xl font-bold text-red-400">{summary.overdue_count || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/30 w-5 h-5" />
            <Input
              placeholder="Search by student, parent, email, or class..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#111] border-[#333] text-white placeholder:text-white/30"
            />
          </div>
        </div>

        {/* Selection Action Bar */}
        {selectedPayments.length > 0 && (
          <div className="mb-4 p-4 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CheckSquare className="w-5 h-5 text-[#FF007F]" />
              <span className="text-white font-medium">{selectedPayments.length} student(s) selected</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearSelection}
                className="text-white/50 hover:text-white"
              >
                Clear
              </Button>
            </div>
            <Button
              onClick={chargePayments}
              disabled={sendingRequests}
              className="bg-[#FF007F] hover:bg-[#D6006A]"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {sendingRequests ? 'Processing...' : 'Charge Selected'}
            </Button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={selectAllPending}
            className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
          >
            <CheckSquare className="w-4 h-4 mr-2" />
            Select All Pending/Overdue
          </Button>
          {selectedPayments.length > 0 && (
            <span className="text-white/50 text-sm">
              Total to charge: {formatPrice(
                filteredPayments
                  .filter(p => selectedPayments.includes(p.id))
                  .reduce((sum, p) => sum + (p.amount || 0), 0)
              )}
            </span>
          )}
        </div>

        {/* Active Filters */}
        {(filterTerm !== 'all' || filterStatus !== 'all') && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-white/50 text-sm">Active filters:</span>
            {filterTerm !== 'all' && (
              <Badge className="bg-[#333] text-white">
                {filterTerm === '6weeks' ? '6 Week Term' : '12 Week Term'}
                <button onClick={() => setFilterTerm('all')} className="ml-2 hover:text-red-400">×</button>
              </Badge>
            )}
            {filterStatus !== 'all' && (
              <Badge className="bg-[#333] text-white">
                {filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                <button onClick={() => setFilterStatus('all')} className="ml-2 hover:text-red-400">×</button>
              </Badge>
            )}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setFilterTerm('all'); setFilterStatus('all'); }}
              className="text-white/50 hover:text-white"
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Payments List */}
        <Card className="bg-[#111] border-[#333]">
          <CardHeader>
            <CardTitle className="text-white">
              Payments ({filteredPayments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No payments found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPayments.map(payment => {
                  const isSelected = selectedPayments.includes(payment.id);
                  const canSelect = payment.payment_status === 'pending' || payment.is_overdue;
                  
                  return (
                  <div
                    key={payment.id}
                    className={`p-4 rounded-lg border transition-all ${
                      isSelected 
                        ? 'bg-[#FF007F]/10 border-[#FF007F]' 
                        : payment.term_length === '12weeks' 
                          ? 'bg-purple-500/5 border-purple-500/30 hover:border-purple-500/50' 
                          : 'bg-blue-500/5 border-blue-500/30 hover:border-blue-500/50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox for pending/overdue payments */}
                      <div className="pt-1">
                        {canSelect ? (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => togglePaymentSelection(payment.id)}
                            className="border-white/30 data-[state=checked]:bg-[#FF007F] data-[state=checked]:border-[#FF007F]"
                          />
                        ) : (
                          <div className="w-4 h-4 rounded border border-white/10 bg-green-500/20 flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 
                            className="text-white font-semibold cursor-pointer hover:text-[#FF007F]"
                            onClick={() => payment.student_id && navigate(`/admin/students/${payment.student_id}`)}
                          >
                            {payment.student_name}
                          </h3>
                          {getTermBadge(payment.term_length)}
                          {getStatusBadge(payment)}
                          {/* Saved Card Indicator */}
                          {payment.has_saved_card ? (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              <Wallet className="w-3 h-3 mr-1" />
                              Card on File
                            </Badge>
                          ) : (payment.payment_status === 'pending' || payment.is_overdue) && (
                            <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                              <Wallet className="w-3 h-3 mr-1" />
                              No Card
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-white/50">Parent</p>
                            <p className="text-white">{payment.parent_name}</p>
                          </div>
                          <div>
                            <p className="text-white/50 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</p>
                            <a href={`mailto:${payment.parent_email}`} className="text-[#FF007F] hover:underline">
                              {payment.parent_email}
                            </a>
                          </div>
                          <div>
                            <p className="text-white/50 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</p>
                            <a href={`tel:${payment.parent_phone}`} className="text-[#FF007F] hover:underline">
                              {payment.parent_phone || 'N/A'}
                            </a>
                          </div>
                          <div>
                            <p className="text-white/50">Classes</p>
                            <p className="text-white">{payment.class_names?.join(', ') || 'N/A'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6 mt-3 text-sm">
                          <div>
                            <span className="text-white/50">Paid: </span>
                            <span className="text-white font-medium">{formatDate(payment.created_at)}</span>
                          </div>
                          <div>
                            <span className="text-white/50">Next Payment Due: </span>
                            <span className={`font-medium ${payment.is_overdue ? 'text-red-400' : 'text-white'}`}>
                              {formatDate(payment.next_payment_due)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right ml-4">
                        <p className="text-2xl font-bold text-white">{formatPrice(payment.amount)}</p>
                        {payment.discount_percent > 0 && (
                          <p className="text-green-400 text-sm">-{payment.discount_percent}% discount</p>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PaymentsManagement;
